import { useState, useEffect } from "react"
import FormField from "../../components/FormField"
import ServiceProviderDetails from "../../components/ServiceProviderDetails"

import {connect} from "react-redux"
import {bindActionCreators} from "redux";
import * as actions from "../../actions"
import CheckBox from "../../components/CheckBox";
import {PAYEE_CATALOG_TYPE_DELIVERY} from "../../constants/payeeCatalog"
import { capitalizeAllWords } from "../../utils/stringUtils";

/**
 * Allow the user to select from a list of delivery catalogs that can deliver to the specified settlement
 * @param {Object} props
 * @param {settlementId} - the area that the delivery catalogs must either be based in or deliver to
 * @param {city} - the name of the area
 * @param {Array} props.deliveryCatalogsArray - (optional) used to pass in the list of delivery catalogs. If not provided, will load delivery catalogs based on the settlement id
 * @param {Object} props.selectedIdMap - used to indicate which delivery catalogs are selected
 * @param {function} props.filter - filters the delivery catalogs, regardless of how they're loaded
 * @param {Object} props.payeeCatalogs- redux
 * @param {Object} props.actions
 * @returns 
 */
const DeliveryServiceSelectionList = ({
    settlementId="",
    city="",
    deliveryCatalogsArray, //used if you want to pass in the list of delivery catalogs to select from
    selectedIdMap={},
    filter=()=>true,
    buttonText="Save Selection",
    title="Select Delivery Method",
    actions,
    payeeCatalogs,
    onChange=()=>{},
    onSubmit=()=>{}
}) => {
    const [deliveryCatalogsBasedInSettlementListener, setDeliveryCatalogsBasedInSettlementListener] = useState(() => () => {})
    const [deliveryCatalogsDeliveringToSettlementListener, setDeliveryCatalogsDeliveringToSettlementListener] = useState(() => () => {})
    const listenToDeliveryCatalogs = async () => {
        const newDeliveryCatalogsBasedInSettlementListener = await actions.fetchSubscribeToDeliveryCatalogsBasedInSettlement(settlementId)
        setDeliveryCatalogsBasedInSettlementListener(() => newDeliveryCatalogsBasedInSettlementListener)
        const newDeliveryCatalogsDeliveringToSettlementListener =  await actions.fetchSubscribeToDeliveryCatalogsDeliveringToSettlement(settlementId)
        setDeliveryCatalogsDeliveringToSettlementListener(() => newDeliveryCatalogsDeliveringToSettlementListener)
    }
    useEffect(() => {
        if (!deliveryCatalogsArray) {
            listenToDeliveryCatalogs()
        }
        return () => {
            if (typeof deliveryCatalogsBasedInSettlementListener === "function") deliveryCatalogsBasedInSettlementListener()
            if (typeof deliveryCatalogsDeliveringToSettlementListener === "function") deliveryCatalogsDeliveringToSettlementListener()
        }
    }, [settlementId])

    const [searchTerm, setSearchTerm] = useState("")
    const deliveryArray = deliveryCatalogsArray ?
                            deliveryCatalogsArray //use the delivery catalogs array if provided 
                            :
                            //otherwise, use the payee catalogs from redux
                            Object.values(payeeCatalogs.payeeCatalogsById)
                                .filter(c => !c.deleted &&
                                             c.config &&
                                             c.config[PAYEE_CATALOG_TYPE_DELIVERY] &&
                                             (
                                                //it is based in the settlement or
                                                c.config[PAYEE_CATALOG_TYPE_DELIVERY].basedInSettlementIds.includes(settlementId) ||
                                                //delivery happens to the specified settlement and the delivery agent picks up from areas its not based in
                                                (
                                                    settlementId in c.config[PAYEE_CATALOG_TYPE_DELIVERY].deliveryPricesBySettlementId &&
                                                    c.config[PAYEE_CATALOG_TYPE_DELIVERY].handlesPickupsFromSettlementsNotBasedIn
                                                )
                                             )
                                )
    const trimmedSearchTerm = searchTerm.trim().toLowerCase()
    const filteredDeliveryArray = deliveryArray.filter(c => 
                                        (!trimmedSearchTerm || c.name.toLowerCase().includes(trimmedSearchTerm)) && //search if search term provided
                                        filter(c) //filter out based on function from parent
                                    )
    //select delivery catalogs in the list
    const [selectedDeliveryCatalogs, setSelectedDeliveryCatalogs] = useState(selectedIdMap)
    //select / deselect delivery providers
    const handleSelectDeliveryCatalogs = (catalog, selected) => {
        const selectionMap = {...selectedDeliveryCatalogs}
        if (selected) selectionMap[catalog.id] = {...catalog}
        else delete selectionMap[catalog.id]
        setSelectedDeliveryCatalogs(selectionMap)
        onChange(selectionMap)
    }
    return (
        <div className="formContainer">
            <div className="formHeader">
                    <div className="h2 text-align-center">{title}</div>
                </div>
            <div className="formBody">
                <FormField
                    value={searchTerm} 
                    onChange={e => setSearchTerm(String(e.target.value))}
                    placeholder={`Search By Delivery Name`}
                    inputIconButton={"close"}
                    onClickInputIconButton={() => setSearchTerm("") }
                />
                <div className="overflow-y-auto max-height-80vh">
                    {
                        filteredDeliveryArray.map(c => {
                            const selected = Boolean(selectedDeliveryCatalogs[c.id])
                            return (
                                <div key={c.id}>
                                    <DeliveryServiceListItem
                                        deliveryCatalog={c}
                                        selected={selected}
                                        onChange={() => handleSelectDeliveryCatalogs(c, !selected)}
                                    />
                                </div>
                            )
                        })
                    }
                    {
                        filteredDeliveryArray.length === 0 ?
                        <div
                            className="padding-05em background-color-light-grey border-radius-8px"
                        >
                            There are no delivery services available in {capitalizeAllWords(city)}</div>
                        :
                        null
                    }
                </div>
            </div>
            <div className="formFooter">
                <button className="button secondary width-100pct" onClick={() => onSubmit(selectedDeliveryCatalogs)}>{buttonText}</button>
            </div>
        </div>
    )
}

const DeliveryServiceListItem = ({
    selected=false,
    deliveryCatalog={},
    onChange=()=>{}
}) => {
    const {name="", payeeName="", payeeLogoImageUrl=""} = deliveryCatalog || {}
    return (
        <div className="display-flex align-items-center flex-direction-row text-align-left width-100pct
        border-bottom-width-1px
        border-bottom-style-solid 
        border-bottom-color-grey
        hover-background-light-grey
        cursor-pointer
        padding-05em">
            <div>
                <CheckBox checked={selected} onChange={onChange}/>
            </div>
            <ServiceProviderDetails 
                imageUrl={payeeLogoImageUrl}
                name={payeeName}
                details={name}
            />
        </div>
    )
}

const mapStateToProps = state => ({
    payeeCatalogs: state.payeeCatalogs,
})

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(actions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliveryServiceSelectionList)